/**
 * InspectionHistory Component (GraphQL version)
 * Displays inspection history using GraphQL API via services-px
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useRealtimeTags } from 'Core/hooks';
import {
	Grid,
	Icon,
	Tooltip,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Checkbox,
	Collapse,
	Button,
	CircularProgress,
} from '@mui/material';
import {
	ArrowUpward,
	ArrowDownward,
	FlagOutlined,
	RemoveCircleOutline,
	ExpandMore,
	ErrorOutline,
} from '@mui/icons-material';
import { format, subDays } from 'date-fns';
import { Loading } from '@gpstrackit/gtc-react-controls';

import {
	getInspectionList,
	exportInspections,
	fetchInspectors,
	fixInspection,
	reloadInspection,
	deleteInspection,
	clearExportState,
	clearFixState,
	clearReloadState,
	clearDeleteState,
} from './redux/slice';
import Pagination from 'Components/uiControls/Pagination';
import FailedInspectionsBanner from './FailedInspectionsBanner';
import FixInspectionModal from './FixInspectionModal';
import DeleteInspectionModal from './DeleteInspectionModal';
import { useFeatureEnabled } from 'Components/FeatureToggle';
import { HasPermission } from 'Core/security';
import { endpoints } from 'Core/defaultValues';
import { NotificationManager } from 'Components/Notifications';
import { getTagColor } from 'Core/data/Helpers';
import { TagsGroup } from 'Components/Tag';

/**
 * Failed Inspection userCodes for subuser permission checking.
 * These map to the EVIR submodule permissions in the Role Editor.
 * UserCodes are environment-specific (assigned by database).
 * Wrapped in arrays because HasPermission converts array items to string for comparison.
 */
const FAILED_INSPECTION_USER_CODES_BY_ENV = {
	development: {
		VIEW_BAD_REPORT_LOG: [371],   // viewbadreportlog::evir:read:inspections - View Failed Inspections
		ADMIN_BAD_REPORT_LOG: [363],  // adminbadreportlog::viewbadreportlog - Delete Failed Inspections
	},
	staging: {
		VIEW_BAD_REPORT_LOG: [371],   // viewbadreportlog::evir:read:inspections - View Failed Inspections
		ADMIN_BAD_REPORT_LOG: [363],  // adminbadreportlog::viewbadreportlog - Delete Failed Inspections
	},
	beta: {
		VIEW_BAD_REPORT_LOG: [371],   // viewbadreportlog::evir:read:inspections - View Failed Inspections
		ADMIN_BAD_REPORT_LOG: [363],  // adminbadreportlog::viewbadreportlog - Delete Failed Inspections
	},
	production: {
		VIEW_BAD_REPORT_LOG: [371],   // viewbadreportlog::evir:read:inspections - View Failed Inspections
		ADMIN_BAD_REPORT_LOG: [363],  // adminbadreportlog::viewbadreportlog - Delete Failed Inspections
	},
};

// Get userCodes for current environment, fallback to staging if unknown
const getFailedInspectionUserCodes = () => {
	const env = endpoints.ENVIROMENT || 'staging';
	return FAILED_INSPECTION_USER_CODES_BY_ENV[env] || FAILED_INSPECTION_USER_CODES_BY_ENV.staging;
};

import './index.scss';
import FilterCard from './filterCard';

const InspectionHistory = () => {
	const { messages } = useIntl();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [queryStrings, setQueryStrings] = useState(null);
	const [inspections, setInspections] = useState(null);
	const [expandedRows, setExpandedRows] = useState(new Set());
	const [selectedRows, setSelectedRows] = useState(new Set());
	const [page, setPage] = useState(1);
	const [rowsPerPage, setRowsPerPage] = useState(25);
	const [fixModalOpen, setFixModalOpen] = useState(false);
	const [fixModalInspection, setFixModalInspection] = useState(null);
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);
	const [deleteModalInspection, setDeleteModalInspection] = useState(null);
	const [isExportingSelected, setIsExportingSelected] = useState(false);
	const [isExportingAll, setIsExportingAll] = useState(false);
	const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
	const [lastDeletedInspectionId, setLastDeletedInspectionId] = useState(null);

	// Get companyId and user from Redux state (same pattern as inspection builder)
	const user = useSelector((state) => state.securityRedux?.user);
	const catalogTags = useRealtimeTags({ trackFields: ['id', 'label', 'disabled', 'tagPath', 'tags'] })

	// Special tag ID that grants access to all tags
	const TAG_ID_ALL_TAGS = -99;

	// Compute user's accessible tag IDs for auto-filtering inspections
	const userAccessibleTagIds = useMemo(() => {
		if (!catalogTags?.length) {
			return null; // No tags available yet, don't filter
		}

		// Parse user's tag IDs
		const userTagIds = user?.tagIds ? JSON.parse(user.tagIds) : [];
		const hasAllTagsPermission = userTagIds.includes(TAG_ID_ALL_TAGS);

		// If user has all tags permission, don't add filter (show all)
		if (hasAllTagsPermission) {
			return null;
		}

		// Get tags that are not disabled (user has direct access)
		const enabledTags = catalogTags.filter((tag) => tag?.disabled === false && tag?.id !== TAG_ID_ALL_TAGS);

		// Return the IDs of tags the user has access to
		const accessibleIds = enabledTags.map((tag) => String(tag.id));

		// If no accessible tags, return empty array to show nothing
		return accessibleIds.length > 0 ? accessibleIds : [];
	}, [catalogTags, user?.tagIds]);

	// Feature flag to enable/disable Fix functionality (disabled by default, shows "Reload" instead)
	const showFixInspection = useFeatureEnabled('evir-px-fix-inspection', false);

	// Permission checks for failed inspection elements
	// For subusers, check 'evir' siteOption + specific userCode in user.options
	// For main users, checking 'evir' siteOption alone grants full access
	const FAILED_INSPECTION_USER_CODES = getFailedInspectionUserCodes();
	const canViewBadReportLog = HasPermission(user, 'evir', FAILED_INSPECTION_USER_CODES.VIEW_BAD_REPORT_LOG);
	const canAdminBadReportLog = HasPermission(user, 'evir', FAILED_INSPECTION_USER_CODES.ADMIN_BAD_REPORT_LOG);

	// Access state directly in the UI
	const inspectionList = useSelector((state) => state.inspectionHistoryRedux?.inspectionList || []);
	const inspectionListCount = useSelector((state) => state.inspectionHistoryRedux?.inspectionListCount || 0);
	const loading = useSelector((state) => state.inspectionHistoryRedux?.loading || false);
	const driverOptions = useSelector((state) => state.inspectionHistoryRedux?.inspectors || []);
	const isApplyingFix = useSelector((state) => state.inspectionHistoryRedux?.fixingInspection || false);
	const reloadingInspectionId = useSelector((state) => state.inspectionHistoryRedux?.reloadingInspectionId || null);
	// Export state
	const exporting = useSelector((state) => state.inspectionHistoryRedux?.exporting || false);
	const exportDownloadUrl = useSelector((state) => state.inspectionHistoryRedux?.exportDownloadUrl || null);
	const exportError = useSelector((state) => state.inspectionHistoryRedux?.exportError || null);
	// Fix inspection result state
	const lastFixedInspectionId = useSelector((state) => state.inspectionHistoryRedux?.lastFixedInspectionId || null);
	const fixInspectionError = useSelector((state) => state.inspectionHistoryRedux?.fixInspectionError || null);
	// Reload inspection result state
	const lastReloadedInspection = useSelector((state) => state.inspectionHistoryRedux?.lastReloadedInspection || null);
	const reloadInspectionError = useSelector((state) => state.inspectionHistoryRedux?.reloadInspectionError || null);
	// Delete inspection state
	const deletingInspectionId = useSelector((state) => state.inspectionHistoryRedux?.deletingInspectionId || null);
	const deleteInspectionError = useSelector((state) => state.inspectionHistoryRedux?.deleteInspectionError || null);

	// Initialize query strings when component mounts
	useEffect(() => {
		setQueryStrings({
			page: 1,
			perPage: rowsPerPage,
			sort: 'startTime',
			sortDir: 'desc',
			dates: [
				{ startTime: subDays(new Date(), 7).toISOString(), endTime: new Date().toISOString() }
			],
		});
	}, []);

	useEffect(() => {
		if (queryStrings) {
			// Auto-filter by user's accessible tags if:
			// 1. userAccessibleTagIds is an array (not null, which means "show all")
			// 2. User hasn't explicitly selected specific tags in the filter
			const hasUserSelectedTags = queryStrings.assetTagIds && queryStrings.assetTagIds.length > 0;
			const shouldAutoFilter = userAccessibleTagIds !== null && !hasUserSelectedTags;

			const effectiveQueryStrings = shouldAutoFilter
				? { ...queryStrings, assetTagIds: userAccessibleTagIds }
				: queryStrings;
			dispatch(getInspectionList({ queryStrings: effectiveQueryStrings }));
		}
	}, [queryStrings, dispatch, userAccessibleTagIds]);

	// Fetch drivers for the Fix modal
	useEffect(() => {
		dispatch(fetchInspectors());
	}, [dispatch]);

	// Handle export result
	useEffect(() => {
		if (exportDownloadUrl) {
			setIsExportingSelected(false);
			setIsExportingAll(false);
			// Export is processed in background, no immediate download
			dispatch(clearExportState());
		}
	}, [exportDownloadUrl, dispatch]);

	// Handle export error
	useEffect(() => {
		if (exportError) {
			console.error('[InspectionHistory] Failed to export inspections', exportError);
			NotificationManager.error(
				messages['exportFailed'] || 'Export failed',
				messages['error'] || 'Error',
				4000,
				null,
				null,
				'error',
				null
			);
			setIsExportingSelected(false);
			setIsExportingAll(false);
			dispatch(clearExportState());
		}
	}, [exportError, dispatch, messages]);

	// Handle fix inspection result
	useEffect(() => {
		if (lastFixedInspectionId) {
			// Update local state to reflect the change (slice also updates inspectionList)
			setInspections((prevInspections) =>
				prevInspections.map((insp) =>
					insp.id === lastFixedInspectionId
						? { ...insp, failed: false, failureReason: null }
						: insp
				)
			);

			NotificationManager.success(
				messages['evirPx_inspection_fixed_success'] || 'Inspection has been fixed successfully.',
				messages['evirPx_inspection_fixed'] || 'Inspection Fixed',
				5000,
				null,
				null,
				'notification-bottom'
			);
			dispatch(clearFixState());
		}
	}, [lastFixedInspectionId, dispatch, messages]);

	// Handle fix inspection error
	useEffect(() => {
		if (fixInspectionError) {
			NotificationManager.error(
				fixInspectionError,
				messages['evirPx_error'] || 'Error',
				5000,
				null,
				null,
				'notification-bottom'
			);
			dispatch(clearFixState());
		}
	}, [fixInspectionError, dispatch, messages]);

	// Handle reload inspection result
	useEffect(() => {
		if (lastReloadedInspection) {
			const { inspectionId, inspection } = lastReloadedInspection;

			if (inspection?.failed) {
				// Still failed - show error message
				NotificationManager.error(
					messages['evirPx_failed_inspection_not_fixed'] || 'The failed inspection has not been fixed.',
					messages['evirPx_failed_inspection'] || 'Failed Inspection',
					5000,
					null,
					null,
					'notification-bottom'
				);
			} else {
				// No longer failed - show success message and update local state
				NotificationManager.success(
					messages['evirPx_failed_inspection_fixed'] || 'The inspection has been successfully processed.',
					messages['evirPx_inspection_fixed'] || 'Inspection Fixed',
					5000,
					null,
					null,
					'notification-bottom'
				);

				// Update local inspections state to remove the failed status (slice also updates inspectionList)
				setInspections((prevInspections) =>
					prevInspections.map((insp) =>
						insp.id === inspectionId
							? { ...insp, failed: false, failureReason: null }
							: insp
					)
				);
			}
			dispatch(clearReloadState());
		}
	}, [lastReloadedInspection, dispatch, messages]);

	// Handle reload inspection error (EVIRPX-655)
	useEffect(() => {
		if (reloadInspectionError) {
			NotificationManager.error(
				messages['evirPx_failed_inspection_not_fixed'] || 'The failed inspection has not been fixed.',
				messages['evirPx_failed_inspection'] || 'Failed Inspection',
				5000,
				null,
				null,
				'notification-bottom'
			);
			dispatch(clearReloadState());
		}
	}, [reloadInspectionError, dispatch, messages]);

	// Handle delete inspection result
	useEffect(() => {
		// When deletingInspectionId becomes null and we had a lastDeletedInspectionId, the delete finished
		if (deletingInspectionId === null && lastDeletedInspectionId) {
			if (deleteInspectionError) {
				// Delete failed - show error notification
				console.error('[InspectionHistory] Failed to delete inspection', deleteInspectionError);
				NotificationManager.error(
					messages['evirPx_delete_error'] || 'Failed to delete inspection. Please try again.',
					messages['evirPx_error'] || 'Error',
					5000,
					null,
					null,
					'notification-bottom'
				);
			} else {
				// Delete succeeded - show success notification and update local state
				setInspections((prevInspections) =>
					prevInspections.filter((insp) => insp.id !== lastDeletedInspectionId)
				);
				NotificationManager.success(
					messages['evirPx_inspection_deleted'] || 'The inspection has been deleted.',
					messages['evirPx_inspection_deleted_title'] || 'Inspection Deleted',
					5000,
					null,
					null,
					'notification-bottom'
				);
			}
			// Clear the tracking state
			setLastDeletedInspectionId(null);
			dispatch(clearDeleteState());
		}
	}, [deletingInspectionId, deleteInspectionError, lastDeletedInspectionId, dispatch, messages]);

	useEffect(() => {
		if (inspectionList) {
			const transformedInspections = inspectionList.map((inspection, index) => {
				// Parse the data field if it's a JSON string (GraphQL returns AWSJSON)
				const inspectionData = typeof inspection.data === 'string'
					? JSON.parse(inspection.data)
					: inspection.data || {};

				// Get assets from the parsed data (GraphQL structure)
				const inspectionAssets = inspectionData.inspectionAssets || [];
				const firstAsset = inspectionAssets[0] || {};

				// Handle both nested (real EVIR) and flat (test data) structures
				const assetData = firstAsset.asset || firstAsset;

				// Get asset type label from zoneLayoutName (stored in inspection data)
				let firstAssetTypeLabel = '';
				if (typeof firstAsset.zoneLayoutName === 'object') {
					firstAssetTypeLabel = firstAsset.zoneLayoutName?.languageLabel || '';
				} else {
					firstAssetTypeLabel = firstAsset.zoneLayoutName || '';
				}

				const assetViewModel = {
					assets: inspectionAssets.length > 0 ? inspectionAssets : null,
					assetsExceptFirst: inspectionAssets.slice(1) || [],
					firstAssetCategory: assetData.assetCategory || firstAsset.assetCategory || '',
					// Get assetId from the data JSON (PX integer ID after migration)
					firstAssetId: assetData.assetId || firstAsset.assetId || '',
					// Fall back to assetId if assetName is not available
					firstAssetName: assetData.assetName || firstAsset.assetName || assetData.assetId?.toString() || firstAsset.assetId?.toString() || '',
					// Use the looked-up label from assetTypes mapping
					firstAssetType: firstAssetTypeLabel,
					multipleAssets: inspectionAssets.length > 1,
				};

				// Get tag IDs from inspection response (or fallback to data JSON for backward compatibility)
				const extractedTagIds = inspection.asset_tagIds || inspectionData.assetTagIds || [];

				const isInspectionIncomplete = (assets) => {
					let isIncomplete = false;

					assets?.forEach(asset => {
						if (asset.inspectionZones) {
							asset.inspectionZones.forEach(zone => {
								if (zone.verificationType === 'uninspected') {
									isIncomplete = true;
								}
							});
						}
					});

					return isIncomplete;
				};

				// Get inspection type info from parsed data
				const inspectionDetail = inspectionData.inspectionDetail || {};

				// Parse driver name from GraphQL driver relation or fallback to inspectionData
				const driverName = inspection.driver?.name || '';
				let lastName = '';
				let firstName = '';

				if (driverName) {
					// GraphQL returns "LastName, FirstName" format in driver.name
					const nameParts = driverName.split(',').map(s => s.trim());
					lastName = nameParts[0] || '';
					firstName = nameParts[1] || '';
				} else {
					// Fallback to inspector info stored in inspection data JSON (from migration)
					firstName = inspectionData.inspectorFirstName || '';
					lastName = inspectionData.inspectorLastName || '';
				}

				return {
					index: index,
					id: inspection.id,
					severity: inspection.maxSeverity,
					// GraphQL returns startTime directly as ISO string
					startDate: inspection.startTime ? format(new Date(inspection.startTime), 'MM/dd/yyyy') : '',
					startTime: inspection.startTime ? format(new Date(inspection.startTime), 'hh:mm:ss a') : '',
					assetViewModel: assetViewModel,
					// INSP. TYPE: Use category.name (e.g., "Pre-Trip") from GraphQL response
					inspectionType: inspection.category?.name
						|| inspectionDetail.inspectionDetailMetadata?.inspectionType
						|| '',
					// INSP. NAME: Use type.name (e.g., "judy pre-inspection") from GraphQL response
					inspectionName: inspectionDetail.inspectionDetailName?.languageLabel || inspection.type?.name || '',
					inspector: `${lastName}, ${firstName}`.replace(/^, |, $/g, ''),
					incompleteInspection: isInspectionIncomplete(inspectionAssets),
					failed: inspection.failed || false,
					failureReason: inspection.failureReason || null,
					// Asset tag IDs for displaying tags column (extracted from inspection data JSON)
					assetTagIds: extractedTagIds,
				};
			});
			setInspections(transformedInspections);
			setHasLoadedOnce(true);
		}
	}, [inspectionList]);

	useEffect(() => {
		setSelectedRows((prevSelected) => {
			if (!prevSelected.size) {
				return prevSelected;
			}

			const validIds = new Set(inspections.map(({ id }) => id) || []);
			let changed = false;
			const nextSelected = new Set();

			prevSelected.forEach((id) => {
				if (validIds.has(id)) {
					nextSelected.add(id);
				} else {
					changed = true;
				}
			});

			if (!changed && nextSelected.size === prevSelected.size) {
				return prevSelected;
			}

			return nextSelected;
		});
	}, [inspections]);

	const handlePageChange = (event, newPage) => {
		setPage(newPage);
		setQueryStrings((prev) => ({
			...prev,
			page: newPage,
		}));
	};

	const handleRowsPerPageChange = (newRowsPerPage) => {
		const newPerPage = newRowsPerPage === 'ALL' ? inspectionListCount : newRowsPerPage;
		setRowsPerPage(newPerPage);
		setPage(1);
		setQueryStrings((prev) => ({
			...prev,
			page: 1,
			perPage: newPerPage,
		}));
	};

	const handleSort = (field) => {
		setQueryStrings((prev) => {
			const newSortDir = prev.sort !== field ? 'desc' : (prev.sortDir === 'asc' ? 'desc' : 'asc');
			return {
				...prev,
				sort: field,
				sortDir: newSortDir,
				page: 1,
			};
		});
		setPage(1);
	};

	const handleQueryStringsChange = (filteredQueryStrings) => {
		setPage(1);
		setQueryStrings((prev) => {
			if (!prev) {
				return prev;
			}

			const { page, perPage, sort, sortDir } = prev;
			const { severities, ...rest } = filteredQueryStrings;
			const severitiesField = severities && severities.length > 0
				? { severities: severities.flatMap(item => item.split(',')) }
				: {};
			return {
				page: 1,
				perPage,
				sort,
				sortDir,
				...rest,
				...severitiesField,
			};
		});
	};

	const getSeverityIcon = (severity) => {
		switch (severity) {
			case 63:
				return <img src='/assets/icons/severity-minor.svg' alt="Minor" />;
			case 127:
				return <img src='/assets/icons/severity-minor.svg' alt="Minor" />;
			case 255:
				return <img src='/assets/icons/severity-major.svg' alt="Major" />;
			default:
				return <img src='/assets/icons/severity-none.svg' alt="None" />;
		}
	};

	const handleAssetDetailsClick = (assetId) => {
		navigate(`/app/evir-px/asset-details/${assetId}`, {
			state: {
				returnPath: '/app/evir-px',
				returnTitle: messages['evirPx_inspection_history'],
			},
		});
	};

	const handleInspectionDetailsClick = (inspectionId, inspectionType) => {
		navigate(`/app/evir-px/inspection-details/${inspectionId}`, {
			state: {
				returnPath: '/app/evir-px',
				returnTitle: messages['evirPx_inspection_history'],
				inspectionType,
			},
		});
	};

	const handleExpandRow = (inspectionId) => {
		setExpandedRows((prev) => {
			const newSet = new Set(prev);

			if (newSet.has(inspectionId)) {
				newSet.delete(inspectionId);
			} else {
				newSet.add(inspectionId);
			}

			return newSet;
		});
	};

	const allRowsSelected = inspections && inspections.length > 0 && selectedRows.size === inspections.length;
	const someRowsSelected = selectedRows.size > 0 && !allRowsSelected;

	// Count failed inspections for the badge
	const failedCount = useMemo(() => {
		return (inspections || []).filter((insp) => insp.failed === true).length;
	}, [inspections]);

	const downloadCsv = (fileReference, fallbackFileName = 'inspections.csv') => {
		if (typeof window === 'undefined' || typeof document === 'undefined') {
			throw new Error('CSV download is not supported in this environment');
		}

		const normalizedReference = `${fileReference ?? ''}`.trim();
		if (!normalizedReference) {
			throw new Error('Missing CSV file reference');
		}

		const isRemoteUrl = /^(https?:)?\/\//i.test(normalizedReference) || normalizedReference.startsWith('data:');

		if (isRemoteUrl) {
			const tempLink = document.createElement('a');
			tempLink.href = normalizedReference;
			tempLink.target = '_blank';
			tempLink.rel = 'noopener noreferrer';
			tempLink.download = fallbackFileName;
			document.body.appendChild(tempLink);
			tempLink.click();
			document.body.removeChild(tempLink);
			return;
		}

		const blob = new Blob([normalizedReference], { type: 'text/csv;charset=utf-8;' });
		const url = window.URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		link.download = fallbackFileName;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		window.URL.revokeObjectURL(url);
	};

	const notifyExportInfo = (message, title) => {
		NotificationManager.info(message, title, 4000, null, null, 'info', null);
	};

	const handleExportCsv = (inspectionIds) => {
		if (!queryStrings) {
			return;
		}

		const hasSelectedIds = inspectionIds && inspectionIds.length > 0;
		if (hasSelectedIds) {
			setIsExportingSelected(true);
		} else {
			setIsExportingAll(true);
		}
		notifyExportInfo(messages['exportInProcessBody'], messages['exportInProcessTitle']);

		dispatch(exportInspections({ inspectionIds, queryStrings }));
	};

	// Handler for Fix All button in banner - opens the fix modal
	const handleFixAllInspections = (failedInspections) => {
		if (failedInspections && failedInspections.length > 0) {
			// Open modal with the first inspection from the group
			setFixModalInspection(failedInspections[0]);
			setFixModalOpen(true);
		}
	};

	const handleCloseFixModal = () => {
		setFixModalOpen(false);
		setFixModalInspection(null);
	};

	const handleApplyFix = (fixData) => {
		const { inspectionId, driverId } = fixData;
		const suggestedDriver = getSuggestedDriver(fixModalInspection);

		// Check if user is trying to reassign to a different driver
		if (suggestedDriver && driverId !== suggestedDriver.id) {
			NotificationManager.error(
				messages['evirPx_driver_reassignment_not_supported'] ||
				'Driver reassignment is not yet supported. Please accept the suggested driver or contact support.',
				messages['evirPx_feature_not_available'] || 'Feature Not Available',
				5000,
				null,
				null,
				'notification-bottom'
			);
			return;
		}

		// Get driver name for the review
		const driver = driverOptions.find((d) => d.id === driverId) || suggestedDriver;
		if (!driver) {
			NotificationManager.error(
				messages['evirPx_driver_not_found'] || 'Driver not found. Please try again.',
				messages['evirPx_error'] || 'Error',
				5000,
				null,
				null,
				'notification-bottom'
			);
			return;
		}

		// Parse driver name into first and last
		const nameParts = driver.name?.split(', ') || [];
		const lastName = nameParts[0] || '';
		const firstName = nameParts[1] || driver.name || '';

		handleCloseFixModal();

		dispatch(fixInspection({
			inspectionId,
			driverFirstName: firstName,
			driverLastName: lastName,
			comment: 'Fixed via Inspection History',
		}));
	};

	// Handler for Fix link in row - opens the fix modal for single inspection
	const handleOpenFixModal = (inspection) => {
		setFixModalInspection(inspection);
		setFixModalOpen(true);
	};

	// Handler for Reload link - re-fetches the inspection to check if it's still failed
	const handleReloadClick = (inspection) => {
		const inspectionId = inspection.id;
		dispatch(reloadInspection({ inspectionId }));
	};

	// Delete modal handlers
	const handleOpenDeleteModal = (inspection) => {
		setDeleteModalInspection(inspection);
		setDeleteModalOpen(true);
	};

	const handleCloseDeleteModal = () => {
		setDeleteModalOpen(false);
		setDeleteModalInspection(null);
	};

	const handleConfirmDelete = (inspectionId, certified) => {
		// Close modal first, then show spinner in the row
		handleCloseDeleteModal();

		// Track which inspection we're deleting for notification handling
		setLastDeletedInspectionId(inspectionId);

		// Dispatch the delete action
		dispatch(deleteInspection({ inspectionId }));
	};

	// Mock suggested driver based on the inspection
	// TODO: Replace with real driver suggestion based on GPS/location data when backend supports it
	const getSuggestedDriver = (inspection) => {
		if (!inspection) return null;
		return {
			id: 780601420,
			name: 'Martinez, Carlos',
			locationId: '18014184',
			time: '3:49:00 PM',
		};
	};

	const handleSelectAllChange = (event) => {
		if (event.target.checked) {
			setSelectedRows(new Set(inspections.map(({ id }) => id) || []));
		} else {
			setSelectedRows(new Set());
		}
	};

	const handleRowSelectionChange = (inspectionId) => {
		setSelectedRows((prev) => {
			const next = new Set(prev);

			if (next.has(inspectionId)) {
				next.delete(inspectionId);
			} else {
				next.add(inspectionId);
			}

			return next;
		});
	};


	const renderSortableHeader = (label, field) => {
		const isActive = queryStrings?.sort === field;
		const Icon = isActive && queryStrings?.sortDir === 'asc' ? ArrowUpward : ArrowDownward;

		return (
			<div
				className={`sortable-header ${isActive ? 'active' : ''}`}
				onClick={() => handleSort(field)}
			>
				<span>{label}</span>
				<Icon className='sort-icon' />
			</div>
		);
	};

	// Show full-page loading spinner on initial load (before any data has been loaded)
	// Subsequent loads show an overlay spinner on the table
	const isInitialLoading = !hasLoadedOnce;
	if (isInitialLoading) {
		return (
			<Grid container className='inspection-history-container'>
				<Grid item className='inspection-history-header-container'>
					<div className='heading'>
						{messages['evirPx_inspection_history'] || 'Inspection History'}
					</div>
				</Grid>
				<Grid item className='inspection-history-loading-container'>
					<Loading />
				</Grid>
			</Grid>
		);
	}

	return (
		<Grid container className='inspection-history-container'>
			{canViewBadReportLog && (
				<Grid item xs={12}>
					<FailedInspectionsBanner
						inspections={inspections || []}
						onFixAll={handleFixAllInspections}
					/>
				</Grid>
			)}
			<Grid item className='inspection-history-header-container'>
				<div className='heading'>
					{messages['evirPx_inspection_history'] || 'Inspection History'}
					<span className='record-count'>({inspectionListCount} {messages['evirPx_total'] || 'total'})</span>
					{canViewBadReportLog && failedCount > 0 && (
						<span className='failed-count-badge'>
							<span className='warning-icon'>&#9888;</span>
							{failedCount} {messages['evirPx_failed'] || 'failed'}
						</span>
					)}
				</div>
				<div className='export-buttons-container'>
					<Button
						variant='contained'
						color='primary'
						className='export-button'
						onClick={() => handleExportCsv(Array.from(selectedRows))}
						disabled={Array.from(selectedRows).length === 0 || isExportingSelected}
					>
						<span className='export-button-text'>
							{isExportingSelected
								? messages['exportInProcessTitle']
								: messages['evirPx_export_selected_csv']
							}
						</span>
					</Button>
					<Button
						variant='contained'
						color='primary'
						className='export-button'
						onClick={() => handleExportCsv(null)}
						disabled={inspectionListCount === 0 || isExportingAll}
					>
						<span className='export-button-text'>
							{isExportingAll
								? messages['exportInProcessTitle']
								: messages['evirPx_export_all_csv']
							}
						</span>
					</Button>
				</div>
			</Grid>
			<Grid item className='inspection-history-filters-container'>
				<FilterCard onQueryStringsChange={handleQueryStringsChange} />
			</Grid>
			<Grid item xs={12} className='inspection-history-table-container'>
				{loading && (
					<div className='table-loading-overlay'>
						<Loading />
					</div>
				)}
				<div className='table-wrapper'>
					<div className='table-header-container'>
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell padding="checkbox">
										<Checkbox
											className='select-all-checkbox'
											checked={allRowsSelected}
											indeterminate={someRowsSelected}
											onChange={handleSelectAllChange}
											inputProps={{ 'aria-label': 'select all inspections' }}
										/>
									</TableCell>
									<TableCell>
										{renderSortableHeader(messages['evirPx_Severity']?.toUpperCase() || 'SEVERITY', 'severity')}
									</TableCell>
									<TableCell>
										{renderSortableHeader(messages['evirPx_Date']?.toUpperCase() || 'DATE', 'startTime')}
									</TableCell>
									<TableCell>
										{messages['evirPx_Time']?.toUpperCase() || 'TIME'}
									</TableCell>
									<TableCell>
										{messages['evirPx_Asset_Number']?.toUpperCase() || 'ASSET NUMBER'}
									</TableCell>
									<TableCell>
										{messages['evirPx_Asset_Type']?.toUpperCase() || 'ASSET TYPE'}
									</TableCell>
									<TableCell>
										{renderSortableHeader(messages['evirPx_Insp_Name']?.toUpperCase() || 'INSP. NAME', 'type_name')}
									</TableCell>
									<TableCell>
										{renderSortableHeader(messages['evirPx_Insp_Type']?.toUpperCase() || 'INSP. TYPE', 'category_name')}
									</TableCell>
									<TableCell>
										{renderSortableHeader(messages['evirPx_Inspector']?.toUpperCase() || 'INSPECTOR', 'inspectorName')}
									</TableCell>
									<TableCell>
										{messages['evirPx_Tags']?.toUpperCase() || 'TAGS'}
									</TableCell>
									<TableCell>
										{messages['evirPx_actions']?.toUpperCase() || 'ACTIONS'}
									</TableCell>
								</TableRow>
							</TableHead>
						</Table>
					</div>
					<div className='table-body-container'>
						<Table size="small">
							<TableBody>
								{(inspections || []).length === 0 && !loading && (
									<TableRow>
										<TableCell colSpan={11} align="center">
											{messages['evirPx_No_Results_Found'] || 'No Results Found'}
										</TableCell>
									</TableRow>
								)}
								{(inspections || []).map((inspection) => {
									const isExpanded = expandedRows.has(inspection.id);
									return (
										<React.Fragment key={inspection.id}>
											<TableRow hover>
												<TableCell padding="checkbox" >
													<Checkbox
														className='select-checkbox'
														checked={selectedRows.has(inspection.id)}
														onChange={() => handleRowSelectionChange(inspection.id)}
														inputProps={{ 'aria-label': `select inspection ${inspection.id}` }}
													/>
												</TableCell>
												<TableCell >
													<div className='severity-cell'>
														{getSeverityIcon(inspection.severity)}
														{canViewBadReportLog && inspection.failed === true && (
															<Tooltip title={inspection.failureReason || 'Inspection failed'}>
																<ErrorOutline className='inspection-failed-icon' />
															</Tooltip>
														)}
													</div>
												</TableCell>
												<TableCell >{inspection.startDate}</TableCell>
												<TableCell >{inspection.startTime}</TableCell>
												<TableCell >
													<div className='asset-cell'>
														<a className='asset-details-link' onClick={() => handleAssetDetailsClick(inspection.assetViewModel.firstAssetId)}>
															{inspection.assetViewModel.firstAssetName}
														</a>
														{inspection.assetViewModel.firstAssetCategory === 'user' && (
															<Tooltip title={messages['evirPx_User_Created_Asset'] || 'User Created Asset'}>
																<FlagOutlined className='user-asset-icon' />
															</Tooltip>
														)}
													</div>
												</TableCell>
												<TableCell>
													<Tooltip title={inspection.assetViewModel.firstAssetType || ''} placement="top">
														<span className="asset-type-text">{inspection.assetViewModel.firstAssetType}</span>
													</Tooltip>
												</TableCell>
												<TableCell >
													<div className='inspection-name-cell'>
														<Tooltip title={inspection.assetViewModel.firstAssetType || ''} placement="top">
															<a className='inspection-details-link' onClick={() => handleInspectionDetailsClick(inspection.id, inspection.inspectionName)}>
																{inspection.inspectionName}
															</a>
														</Tooltip>
														{inspection.incompleteInspection && (
															<Tooltip title={messages['evirPx_Incomplete_Inspection'] || 'Incomplete Inspection'}>
																<RemoveCircleOutline className='incomplete-inspection-icon' />
															</Tooltip>
														)}
													</div>
												</TableCell>
												<TableCell >{inspection.inspectionType}</TableCell>
												<TableCell >{inspection.inspector}</TableCell>
												<TableCell>
													<div className='tags-cell'>
														{inspection.assetTagIds && inspection.assetTagIds.length > 0 && (
															<TagsGroup
																options={inspection.assetTagIds
																	.map((tagId) => catalogTags.find((t) => String(t.id) === String(tagId)))
																	.filter(Boolean)
																	.map((tag) => ({
																		...tag,
																		color: `#${getTagColor(tag.color)}`,
																	}))}
																nameField="label"
																colorField="color"
																alwaysGroup={true}
															/>
														)}
													</div>
												</TableCell>
												<TableCell >
													<div className='actions-cell'>
														{inspection.failed === true && (canViewBadReportLog || canAdminBadReportLog) && (
															<span className='action-links'>
																{canViewBadReportLog && (
																	<>
																		{showFixInspection ? (
																			<a className='action-link fix-link' onClick={() => handleOpenFixModal(inspection)}>{messages['evirPx_fix'] || 'Fix'}</a>
																		) : (
																			<span className='reload-container'>
																				{reloadingInspectionId === inspection.id ? (
																					<CircularProgress size={14} className='reload-spinner' />
																				) : (
																					<a
																						className='action-link reload-link'
																						onClick={() => handleReloadClick(inspection)}
																					>
																						{messages['evirPx_reload'] || 'Reload'}
																					</a>
																				)}
																			</span>
																		)}
																		{canAdminBadReportLog && <span className='action-separator'>|</span>}
																	</>
																)}
																{canAdminBadReportLog && (
																	<span className='delete-container'>
																		{deletingInspectionId === inspection.id ? (
																			<CircularProgress size={14} className='delete-spinner' />
																		) : (
																			<a
																				className='action-link delete-link'
																				onClick={() => handleOpenDeleteModal(inspection)}
																			>
																				{messages['evirPx_delete'] || 'Delete'}
																			</a>
																		)}
																	</span>
																)}
															</span>
														)}
														{inspection.assetViewModel.multipleAssets && (
															<ExpandMore
																className={`expand-assets-icon ${isExpanded ? 'expanded' : ''}`}
																onClick={() => handleExpandRow(inspection.id)}
															/>
														)}
													</div>
												</TableCell>
											</TableRow>
											{inspection.assetViewModel.multipleAssets && (
												<TableRow>
													<TableCell colSpan={11} style={{ padding: 0 }}>
														<Collapse in={isExpanded} timeout="auto" unmountOnExit>
															<div className='additional-assets-container'>
																<div className='additional-assets-header'>
																	{messages['evirPx_Additional_Asset_Information']?.toUpperCase() || 'ADDITIONAL ASSET INFORMATION'}
																</div>
																<div className='additional-asset-list'>
																	{inspection.assetViewModel.assetsExceptFirst.map((assetData, idx) => (
																		<div key={idx} className='additional-asset-item'>
																			<a
																				className='asset-details-link'
																				onClick={() => handleAssetDetailsClick(assetData.assetId || assetData.asset?.assetId)}
																			>
																				{assetData.assetName || assetData.asset?.assetName || ''}
																			</a>
																			<span className='asset-type-label'>
																				{typeof assetData.zoneLayoutName === 'object'
																					? assetData.zoneLayoutName?.languageLabel || ''
																					: assetData.zoneLayoutName || ''}
																			</span>
																		</div>
																	))}
																</div>
															</div>
														</Collapse>
													</TableCell>
												</TableRow>
											)}
										</React.Fragment>
									);
								})}
							</TableBody>
						</Table>
					</div>
				</div>
				<Pagination
					page={page}
					rowsPerPage={rowsPerPage}
					count={inspectionListCount}
					onChangePage={handlePageChange}
					onChangeRowsPerPage={handleRowsPerPageChange}
					valuesPerPage={[10, 25, 50]}
					hideAllInPagination
				/>
			</Grid>
			<FixInspectionModal
				open={fixModalOpen}
				onClose={handleCloseFixModal}
				inspection={fixModalInspection}
				suggestedDriver={getSuggestedDriver(fixModalInspection)}
				driverOptions={driverOptions}
				onApplyFix={handleApplyFix}
			/>
			<DeleteInspectionModal
				open={deleteModalOpen}
				onClose={handleCloseDeleteModal}
				inspection={deleteModalInspection}
				onConfirmDelete={handleConfirmDelete}
			/>
		</Grid>
	);
};

export default InspectionHistory;
